// frontend/src/pages/Login.jsx

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/Common/Button'
import { FileText, Github, Mail } from 'lucide-react'
import toast from 'react-hot-toast'

export const Login = () => {
  const navigate = useNavigate()
  const { register, login, startGoogleOAuth, startGitHubOAuth, startYandexOAuth } = useAuth()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('login') // 'login' или 'register'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    firstName: '',
    lastName: '',
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault()
    if (!formData.email || !formData.password) {
      toast.error('Заполните все поля')
      return
    }

    setLoading(true)
    try {
      await register(formData.email, formData.password, formData.username, formData.firstName, formData.lastName)
      navigate('/login')
    } catch (error) {
      // Ошибка уже обработана в hook
    } finally {
      setLoading(false)
    }
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault()
    if (!formData.email || !formData.password) {
      toast.error('Заполните все поля')
      return
    }

    setLoading(true)
    try {
      await login(formData.email, formData.password)
      navigate('/projects')
    } catch (error) {
      // Ошибка уже обработана в hook
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleOAuth = async () => {
    setLoading(true)
    try {
      const response = await startGoogleOAuth()
      // useAuth проглатывает ошибки - проверяем результат явно
      if (!response || !response.authorization_url) {
        toast.error('Не удалось получить URL авторизации Google')
        setLoading(false)
        return
      }
      // После OAuth возвращаемся на /projects (не на /login!)
      sessionStorage.setItem('oauth_return_url', '/projects')
      window.location.href = response.authorization_url
    } catch (error) {
      console.error('Google OAuth start error:', error)
      toast.error('Ошибка при запуске Google OAuth')
      setLoading(false)
    }
  }

  const handleGitHubOAuth = async () => {
    setLoading(true)
    try {
      const response = await startGitHubOAuth()
      if (!response || !response.authorization_url) {
        toast.error('Не удалось получить URL авторизации GitHub')
        setLoading(false)
        return
      }
      sessionStorage.setItem('oauth_return_url', '/projects')
      window.location.href = response.authorization_url
    } catch (error) {
      console.error('GitHub OAuth start error:', error)
      toast.error('Ошибка при запуске GitHub OAuth')
      setLoading(false)
    }
  }

  const handleYandexOAuth = async () => {
    setLoading(true)
    try {
      const response = await startYandexOAuth()
      if (!response || !response.authorization_url) {
        toast.error('Не удалось получить URL авторизации Яндекс')
        setLoading(false)
        return
      }
      sessionStorage.setItem('oauth_return_url', '/projects')
      window.location.href = response.authorization_url
    } catch (error) {
      console.error('Yandex OAuth start error:', error)
      toast.error('Ошибка при запуске Yandex OAuth')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
        {/* Header */}
        <div className="flex items-center justify-center mb-8">
          <FileText size={40} className="text-blue-600" />
          <h1 className="text-2xl font-bold ml-3">Экспертиza</h1>
        </div>

        <h2 className="text-xl font-bold text-center mb-6">
          Вход в систему
        </h2>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('login')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              activeTab === 'login'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Вход
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              activeTab === 'register'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Регистрация
          </button>
        </div>

        {/* Login Form */}
        {activeTab === 'login' && (
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Пароль *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <Button
              variant="primary"
              size="lg"
              type="submit"
              loading={loading}
              className="w-full"
            >
              Войти
            </Button>
          </form>
        )}

        {/* Register Form */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegisterSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Имя
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Иван"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Фамилия
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Иванов"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Имя пользователя
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="john_doe"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Пароль *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <Button
              variant="primary"
              size="lg"
              type="submit"
              loading={loading}
              className="w-full"
            >
              Создать аккаунт
            </Button>
          </form>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">или</span>
          </div>
        </div>

        {/* OAuth Buttons */}
        <div className="space-y-3">
{/*           <button */}
{/*             onClick={handleGoogleOAuth} */}
{/*             disabled={loading} */}
{/*             className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed" */}
{/*           > */}
{/*             <Mail size={20} /> */}
{/*             <span className="font-medium">Google</span> */}
{/*           </button> */}

{/*           <button */}
{/*             onClick={handleGitHubOAuth} */}
{/*             disabled={loading} */}
{/*             className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed" */}
{/*           > */}
{/*             <Github size={20} /> */}
{/*             <span className="font-medium">GitHub</span> */}
{/*           </button> */}

          <button
            onClick={handleYandexOAuth}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* Логотип Яндекса (буква Я в красном круге) */}
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-red-600 text-white text-xs font-bold">
              Я
            </span>
            <span className="font-medium">Яндекс</span>
          </button>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Используя эту систему, вы соглашаетесь с нашей политикой конфиденциальности
        </p>
      </div>
    </div>
  )
}
